import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import confirm from 'modules/confirm';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { Button, Loader } from 'ncoded-component-library';
import { getUserFullName, isEmail, normalizeEmail } from 'utils';
import { getMainGroupInfoPayloadValues } from '../AddGroupMember/components/BulkAddMember/utils';
import ArrowrightIcon from 'icons/Arrowright.icon';
import TrashIcon from 'icons/Trash.icon';
import PauseIcon from 'icons/Pause.icon';
import {
  DataRoomBuySideGroup,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useRevokeFileAccess from '../../hooks/useRevokeFIleAccess';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import { MainGroupInfoForm } from '../AddGroupMember/components/BulkAddMember/BulkAddMember.component';

import './ManageBuyerGroup.styles.scss';

type ManageBuyerGroupProps = {
  className?: string;
  group: DataRoomBuySideGroup;
};

const ManageBuyerGroup: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageBuyerGroupProps
> = (props, ref) => {
  const { className, group } = props;

  const classes = classNames('manage-buyer-group', className);

  const { name, dataRoomMembers = [] } = group || {};

  const [isLoading, setIsLoading] = useState(false);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { updateBuySideGroup, setBuySideGroups, setSellSideMembers } =
    useContext(MembersContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const initialValues = useMemo(
    () =>
      ({
        name,
        buySideGroupMembers: [],
      } as const),
    [name],
  );

  const numberOfMembersWithAccess = useMemo(
    () =>
      dataRoomMembers.filter(
        ({ accessableDataRoomNodesView }) =>
          !!accessableDataRoomNodesView.length,
      ).length,
    [dataRoomMembers],
  );

  const handleOnRevokeAccess = useCallback(
    () => dataRoomApi.members.revokeAccessGroup(group._id),
    [group],
  );

  const handleOnUndoRevokeAccess = useCallback(
    () => dataRoomApi.members.undoRevokeAccessGroup(group._id),
    [group],
  );

  const updateValueOnRevokeSuccess = useCallback(
    (group: DataRoomBuySideGroup) =>
      updateBuySideGroup({
        ...group,
        dataRoomMembers: group.dataRoomMembers.map((member) => ({
          ...member,
          accessableDataRoomNodesView: [],
        })),
      }),
    [updateBuySideGroup],
  );

  const {
    isLoadingRevoke,
    isUndoAvailable,
    hasFileAccess,
    onRevokeAccess,
    onUndoRevokeAccess,
  } = useRevokeFileAccess({
    value: group,
    numberOfAccess: numberOfMembersWithAccess,
    handleOnRevokeAccess,
    handleOnUndoRevokeAccess,
    updateValueOnRevokeSuccess,
    updateValueOnUndoSuccess: updateBuySideGroup,
  });

  const updateMembers = useCallback(
    (data: DataRoomMembers) => {
      const { sellSideMembers, buySideGroups } = data;
      setSellSideMembers(sellSideMembers);
      setBuySideGroups(buySideGroups);
      drawer?.setOpen(false);
    },
    [drawer, setBuySideGroups, setSellSideMembers],
  );

  const onDeleteGroup = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('deleteThisGroup'),
          content: t('deleteThisGroupMessage'),
        },
        true,
      )
    ) {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.deleteGroup(group._id);

        updateMembers(data);
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [group, updateMembers, popServerError, t]);

  const onSubmit = useCallback(
    async (values: MainGroupInfoForm) => {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.updateBuySideGroup(
          group._id,
          getMainGroupInfoPayloadValues(values),
        );

        updateMembers(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [group, popServerError, updateMembers],
  );

  if (doesntHaveAccess) return null;

  return (
    <>
      <SideDrawer ref={drawerRef} className={classes}>
        <p className="drawer-title">{t('manageBuyerGroup')}</p>
        {drawer?.open && (
          <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({
              invalid,
              values: { buySideGroupMembersUnsubmited },
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit}>
                <Field
                  name="name"
                  label={t('groupName')}
                  description={t('helpColleaguesUnderstandRepresents')}
                  component={InputField}
                  validate={validations.required()}
                  required
                />

                <div className="key-actions">
                  <p>{t('keyActions')}</p>

                  <div>
                    {hasFileAccess && (
                      <Button
                        onClick={onRevokeAccess}
                        icon={PauseIcon}
                        disabled={isLoadingRevoke}
                        variant="link"
                      >
                        {t('revokeAccessForAll')}
                      </Button>
                    )}
                    <Button
                      onClick={onDeleteGroup}
                      icon={TrashIcon}
                      variant="link"
                      className="warning-action-button"
                    >
                      {t('deleteThisGroup')}
                    </Button>
                  </div>
                </div>

                <div className="members">
                  <p>{t('members')}</p>

                  {dataRoomMembers.map(
                    ({ _id, firstName, lastName, email }) => (
                      <div key={_id}>
                        <p>
                          {getUserFullName(firstName, lastName) || t('invited')}
                        </p>
                        {email && <p>{normalizeEmail(email)}</p>}
                      </div>
                    ),
                  )}
                </div>

                <Field
                  name="buySideGroupMembers"
                  label={t('inviteMoreBuyMembers')}
                  component={TextareaEmailsField}
                  description={`${t('buySideGroupNote')} ${t(
                    'weAreGoingToSendInvitationsViaEmail',
                  )}`}
                />

                <div className="actions">
                  <Button
                    type="submit"
                    variant="outline"
                    iconPosition="right"
                    icon={ArrowrightIcon}
                    disabled={
                      invalid ||
                      (buySideGroupMembersUnsubmited &&
                        !isEmail(buySideGroupMembersUnsubmited))
                    }
                  >
                    {t('save')}
                  </Button>
                </div>
              </form>
            )}
          />
        )}
        {isLoading && <Loader />}
      </SideDrawer>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoadingRevoke}
        onUndo={onUndoRevokeAccess}
      >
        <p>{t('fileAccessRevokedGroup')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default forwardRef(ManageBuyerGroup);
